/**
 * hi-video-stories.js — "Stories de vídeo" (sections/hi-video-stories.liquid).
 *
 * - Cards: vídeos sem som que só carregam/tocam quando entram na tela
 *   (preload="none" + IntersectionObserver). Com prefers-reduced-motion,
 *   ficam parados no pôster.
 * - Player: <dialog> em tela cheia, com som, barra de progresso, contador,
 *   botões anterior/próximo, teclado (← → Esc) e deslizar no celular.
 *   Ao terminar um vídeo, passa para o próximo.
 */
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class HiVideoStories extends HTMLElement {
  #observer = null;
  #index = 0;
  #touchX = null;
  #opener = null;

  connectedCallback() {
    this.cards = [...this.querySelectorAll('.hi-stories__card')];
    this.dialog = this.querySelector('dialog');
    this.player = this.querySelector('.hi-stories__player');
    this.caption = this.querySelector('.hi-stories__caption');
    this.counter = this.querySelector('.hi-stories__counter');
    this.bar = this.querySelector('.hi-stories__progress span');
    if (!this.cards.length || !this.dialog || !this.player) return;

    this.cards.forEach((card) => card.addEventListener('click', () => this.open(Number(card.dataset.index), card)));
    this.querySelector('.hi-stories__close')?.addEventListener('click', () => this.dialog.close());
    this.querySelector('.hi-stories__nav--prev')?.addEventListener('click', () => this.go(-1));
    this.querySelector('.hi-stories__nav--next')?.addEventListener('click', () => this.go(1));
    this.querySelector('.hi-stories__cta')?.addEventListener('click', () => this.dialog.close());

    this.dialog.addEventListener('close', this.#onClose);
    this.dialog.addEventListener('keydown', this.#onKey);
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog.close(); // clique fora do vídeo
    });
    this.player.addEventListener('click', () => (this.player.paused ? this.player.play() : this.player.pause()));
    this.player.addEventListener('timeupdate', this.#onTime);
    this.player.addEventListener('ended', () => this.go(1, true));
    this.dialog.addEventListener('touchstart', (e) => (this.#touchX = e.touches[0].clientX), { passive: true });
    this.dialog.addEventListener('touchend', this.#onSwipe, { passive: true });

    if (!REDUCED) {
      this.#observer = new IntersectionObserver(this.#onVisible, { threshold: 0.4 });
      this.querySelectorAll('.hi-stories__thumb').forEach((v) => this.#observer.observe(v));
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #onVisible = (entries) => {
    for (const { target: video, isIntersecting } of entries) {
      if (isIntersecting) {
        if (!video.src) video.src = video.dataset.src;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
  };

  open(index, opener) {
    this.#opener = opener;
    this.querySelectorAll('.hi-stories__thumb').forEach((v) => v.pause());
    this.dialog.showModal();
    document.documentElement.setAttribute('data-hi-stories-open', '');
    this.show(index);
  }

  show(index) {
    const total = this.cards.length;
    this.#index = (index + total) % total;
    const card = this.cards[this.#index];
    this.player.src = card.dataset.src;
    if (card.dataset.poster) this.player.poster = card.dataset.poster;
    this.player.muted = false;
    this.caption.textContent = card.dataset.caption || '';
    this.counter.textContent = `${this.#index + 1} / ${total}`;
    this.bar.style.width = '0%';
    this.player.play().catch(() => {
      // Autoplay com som bloqueado: toca mudo e o toque no vídeo libera o som.
      this.player.muted = true;
      this.player.play().catch(() => {});
    });
  }

  go(step, auto = false) {
    if (auto && this.#index === this.cards.length - 1) {
      this.dialog.close();
      return;
    }
    this.show(this.#index + step);
  }

  #onTime = () => {
    const { currentTime, duration } = this.player;
    if (duration) this.bar.style.width = `${(currentTime / duration) * 100}%`;
  };

  #onKey = (event) => {
    if (event.key === 'ArrowRight') this.go(1);
    if (event.key === 'ArrowLeft') this.go(-1);
  };

  #onSwipe = (event) => {
    if (this.#touchX === null) return;
    const dx = event.changedTouches[0].clientX - this.#touchX;
    this.#touchX = null;
    if (Math.abs(dx) > 50) this.go(dx < 0 ? 1 : -1);
  };

  #onClose = () => {
    this.player.pause();
    this.player.removeAttribute('src');
    this.player.load();
    document.documentElement.removeAttribute('data-hi-stories-open');
    this.#opener?.focus();
    if (!REDUCED) {
      this.querySelectorAll('.hi-stories__thumb').forEach((v) => {
        const r = v.getBoundingClientRect();
        if (r.bottom > 0 && r.top < innerHeight && v.src) v.play().catch(() => {});
      });
    }
  };
}

if (!customElements.get('hi-video-stories')) customElements.define('hi-video-stories', HiVideoStories);
